import { motion } from "framer-motion";
import {
  Castle,
  Swords,
  Shield,
  Coins,
  Trophy,
  MessageSquare,
} from "lucide-react";
import { IMAGES } from "./config";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: i * 0.08 },
  }),
};

const Card = ({ icon: Icon, title, text, className, testId, index, image }) => (
  <motion.div
    custom={index}
    variants={reveal}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-80px" }}
    data-testid={testId}
    className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-[#111111]/70 backdrop-blur-xl p-8 transition-colors duration-500 hover:border-[#D4AF37]/40 ${className}`}
  >
    {image && (
      <>
        <img
          src={image}
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-25 transition-opacity duration-700 group-hover:opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#080808] via-[#080808]/60 to-transparent" />
      </>
    )}
    <div className="relative z-10">
      <div className="mb-6 inline-flex rounded-xl border border-[#D4AF37]/25 bg-[#D4AF37]/10 p-3 text-[#D4AF37] shadow-[0_0_30px_rgba(212,175,55,0.12)]">
        <Icon className="h-6 w-6" />
      </div>
      <h3 className="font-display mb-3 text-2xl font-semibold tracking-tight text-[#EAEAEA] md:text-3xl">
        {title}
      </h3>
      <p className="max-w-md text-sm leading-relaxed text-[#A3A3A3] md:text-base">
        {text}
      </p>
    </div>
  </motion.div>
);

export const Features = () => (
  <section id="features" data-testid="features-section" className="mx-auto max-w-7xl px-6 py-28 md:py-40">
    <motion.div
      variants={reveal}
      custom={0}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      className="mb-16 md:mb-24"
    >
      <p className="font-accent mb-4 text-xs font-semibold tracking-[0.4em] text-[#D4AF37]">
        RULE THE REALM
      </p>
      <h2 className="font-display max-w-2xl text-4xl font-semibold tracking-tight text-[#EAEAEA] md:text-6xl">
        Everything a true sovereign needs to rise from peasant to legend.
      </h2>
    </motion.div>

    <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
      <Card
        index={1}
        testId="feature-kingdom"
        icon={Castle}
        title="Build Your Kingdom"
        text="Construct buildings, assign workers and generate a steady flow of resources around the clock. Your realm never sleeps — and neither does its treasury."
        className="md:col-span-7 md:row-span-2 min-h-[280px] md:min-h-[420px] flex flex-col justify-end"
        image={IMAGES.knight}
      />
      <Card
        index={2}
        testId="feature-army"
        icon={Swords}
        title="Raise an Army"
        text="Train your hero, grow your strength, and attack or raid other players for gold and glory."
        className="md:col-span-5"
      />
      <Card
        index={3}
        testId="feature-alliances"
        icon={Shield}
        title="Forge Alliances"
        text="Join forces with others, coordinate in alliance chat and defend your lands together."
        className="md:col-span-5"
      />
      <Card
        index={4}
        testId="feature-market"
        icon={Coins}
        title="Master the Market"
        text="Trade resources, bank your gold safely and outsmart rivals in a living player economy."
        className="md:col-span-4"
      />
      <Card
        index={5}
        testId="feature-ranks"
        icon={Trophy}
        title="Climb the Ranks"
        text="Compete on daily quests, bounties and leaderboards to prove you are the realm's finest."
        className="md:col-span-4"
      />
      <Card
        index={6}
        testId="feature-community"
        icon={MessageSquare}
        title="Live Community"
        text="Real-time tavern chat, forums and direct messages keep the kingdom alive day and night."
        className="md:col-span-4"
      />
    </div>
  </section>
);
